/**
 * Latest view component.
 * @module components/theme/View/LatestView
 */

import React from 'react';
import { UniversalLink } from '@plone/volto/components';
import { useSelector } from 'react-redux';

import VodButton from '../../../shared/VodButton';
import RadioLiveSection from '../../../shared/RadioLiveSection';
import RadioScheduleSection from '../../../shared/RadioScheduleSection';
import VideoCastSection from '../../../shared/VideoCastSection';
import LatestNewsAside from '../../../shared/LatestNewsAside';

import useRadioLiveController from '../../../shared/RadioLiveSection/useRadioLiveController';

import { getRadioSchedule } from '../../../../services/api/radioSchedule';
import {
  getLatestContent,
  type LatestContentItem,
} from '../../../../services/api/contentLatest';

import type { RadioScheduleItem } from '../../../shared/RadioScheduleSection/RadioScheduleSection.types';

import { AdManagerSlot } from '../../../../components/AdManagerSlot';

import type { VideoCastSectionItem } from '../../../shared/VideoCastSection/VideoCastSection.types';
import { getLatestYoutubeVideos } from '../../../../services/api/youtubeVideos';

import './LatestView.less';

type Props = {
  content?: any;
};

function normalizeVideoItems(items: any[] = []): VideoCastSectionItem[] {
  return items.map((item) => ({
    id: item.id,
    title: item.title,
    eyebrow: 'Videocast',
    href: item.url,
    image: item.thumbnail,
    ctaLabel: 'Assista agora',
  }));
}

function LatestCardSkeleton() {
  return (
    <article className="latest-view__card latest-view__card--loading">
      <div className="latest-view__card-imageLink">
        <div className="latest-view__card-media">
          <div className="latest-view__loadingImage shimmer" />
        </div>
      </div>

      <div className="latest-view__card-body">
        <div className="latest-view__loadingProgram shimmer" />
        <div className="latest-view__loadingTitle shimmer" />
        <div className="latest-view__card-meta">
          <div className="latest-view__loadingMeta shimmer" />
          <span className="latest-view__card-separator">|</span>
          <div className="latest-view__loadingAction shimmer" />
        </div>
      </div>
    </article>
  );
}

function LatestCard({ item }: { item: LatestContentItem }) {
  const url = item?.['@id'] || '#';
  const title = item?.title || '';
  const image = item?.image || '';
  const programTitle = item?.pogramTitle || '';
  const date = item?.datePublished || '';
  const audioUrl = item?.audio || '';
  const programUrl = item?.programUrl || '';
  const playerMode = useSelector((state: any) => state.player?.mode ?? '');
  const intentPlaying = useSelector(
    (state: any) => !!state.player?.intentPlaying,
  );
  const currentVodUrl = useSelector((state: any) => state.player?.vodUrl || '');

  const isPlayingThis =
    !!audioUrl &&
    playerMode === 'vod' &&
    currentVodUrl === audioUrl &&
    intentPlaying;

  return (
    <article
      className={
        isPlayingThis
          ? 'latest-view__card latest-view__card--playing'
          : 'latest-view__card'
      }
    >
      <UniversalLink
        href={url}
        className="latest-view__card-imageLink"
        aria-label={title}
      >
        <div className="latest-view__card-media">
          {image ? (
            <img
              src={image}
              alt=""
              loading="lazy"
              decoding="async"
              className="latest-view__card-image"
            />
          ) : (
            <div
              className="latest-view__card-imagePlaceholder"
              aria-hidden="true"
            />
          )}
          {isPlayingThis ? (
            <div className="latest-view__nowPlaying">
              <span className="latest-view__equalizer" aria-hidden="true">
                <i />
                <i />
                <i />
              </span>
              <span>Tocando</span>
            </div>
          ) : null}
        </div>
      </UniversalLink>

      <div className="latest-view__card-body">
        {programTitle ? (
          programUrl ? (
            <UniversalLink
              href={programUrl}
              className="latest-view__card-program"
              title={programTitle}
            >
              {programTitle}
            </UniversalLink>
          ) : (
            <p className="latest-view__card-program">{programTitle}</p>
          )
        ) : null}

        <UniversalLink
          href={url}
          className="latest-view__card-titleLink"
          title={title}
        >
          <h2 className="latest-view__card-title">{title}</h2>
        </UniversalLink>

        <div className="latest-view__card-meta">
          {date ? <span className="latest-view__card-date">{date}</span> : null}

          <span className="latest-view__card-separator">|</span>

          <div className="latest-view__card-action">
            <VodButton
              url={audioUrl}
              pageUrl={url}
              title={title}
              subtitle={programTitle}
              image={image}
              label="Ouça agora"
              origin="LatestView"
              analytics={{
                event: 'audio_play',
                section: 'latest.list',
                contentId: url,
                contentType: 'vod',
                label: 'Ouça agora',
              }}
              className={
                isPlayingThis
                  ? 'latest-view__card-play pressable vodButton--active'
                  : 'latest-view__card-play pressable'
              }
            />
          </div>
        </div>
      </div>
    </article>
  );
}

export default function LatestView({ content }: Props) {
  const [items, setItems] = React.useState<LatestContentItem[]>([]);
  const [page, setPage] = React.useState(1);
  const [loadingInitial, setLoadingInitial] = React.useState(true);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [hasMore, setHasMore] = React.useState(true);

  const [scheduleItems, setScheduleItems] = React.useState<RadioScheduleItem[]>(
    [],
  );

  const [videoCastItems, setVideoCastItems] = React.useState<
    VideoCastSectionItem[]
  >([]);

  const [loadingVideoCast, setLoadingVideoCast] = React.useState(true);

  const {
    stations,
    currentStationId,
    intentPlaying,
    isPlaying,
    isLiveMode,
    onPlayStation,
  } = useRadioLiveController({
    analyticsSection: 'latest.sidebar',
    analyticsOrigin: 'LatestView',
  });

  React.useEffect(() => {
    let cancelled = false;

    async function loadInitialData() {
      setLoadingInitial(true);

      try {
        const [latestResult, scheduleResult] = await Promise.all([
          getLatestContent(1),
          getRadioSchedule(),
        ]);

        if (cancelled) return;

        setItems(latestResult);
        setScheduleItems(scheduleResult);
        setHasMore(latestResult.length > 0);
        setPage(1);
      } catch (error) {
        if (cancelled) return;

        setItems([]);
        setScheduleItems([]);
        setHasMore(false);
      } finally {
        if (!cancelled) {
          setLoadingInitial(false);
        }
      }
    }

    loadInitialData();

    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    let cancelled = false;

    async function loadVideoCast() {
      setLoadingVideoCast(true);

      try {
        const result = await getLatestYoutubeVideos();

        if (cancelled) return;

        setVideoCastItems(normalizeVideoItems(result).slice(0, 3));
      } catch (error) {
        if (!cancelled) {
          setVideoCastItems([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingVideoCast(false);
        }
      }
    }

    loadVideoCast();

    return () => {
      cancelled = true;
    };
  }, []);

  async function handleLoadMore() {
    if (loadingMore || !hasMore) return;

    const nextPage = page + 1;
    setLoadingMore(true);

    try {
      const result = await getLatestContent(nextPage);

      if (!result.length) {
        setHasMore(false);
        return;
      }

      setItems((prev) => [...prev, ...result]);
      setPage(nextPage);
    } catch (error) {
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  }

  return (
    <div className="latest-view">
      <div className="latest-view__top">
        <main className="latest-view__main">
          <header className="latest-view__header">
            <h1 className="latest-view__title">
              {content?.title || 'Últimas'}
            </h1>
          </header>

          {loadingInitial ? (
            <div className="latest-view__grid latest-view__grid--loading">
              {Array.from({ length: 6 }).map((_, index) => (
                <LatestCardSkeleton key={index} />
              ))}
            </div>
          ) : items.length ? (
            <>
              <div className="latest-view__grid">
                {items.map((item) => (
                  <LatestCard key={item['@id']} item={item} />
                ))}
              </div>

              {hasMore ? (
                <div className="latest-view__more">
                  <button
                    type="button"
                    className="latest-view__moreButton"
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                  >
                    {loadingMore ? 'Carregando...' : 'Ver mais'}
                  </button>
                </div>
              ) : null}
            </>
          ) : (
            <p className="latest-view__empty">Nenhum conteúdo encontrado.</p>
          )}
        </main>

        <aside className="latest-view__sidebar">
          <div className="latest-view__sidebar-block">
            <RadioLiveSection
              title="Ouça ao vivo"
              variant="compact"
              stations={stations}
              currentStationId={currentStationId}
              isPlaying={isPlaying}
              intentPlaying={intentPlaying}
              isLiveMode={isLiveMode}
              emptyText="Nenhuma emissora disponível no momento."
              onPlayStation={onPlayStation}
            />
          </div>

          <div className="latest-view__sidebar-block">
            <RadioScheduleSection
              title="Programação"
              items={scheduleItems}
              footerLabel="Ver programação completa"
              href="/programacao"
            />
          </div>
          <div className="latest-view__sidebar-block">
            <AdManagerSlot
              slotKey="master"
              className="ad-manager-slot--master"
            />
          </div>
        </aside>
      </div>

      <div className="latest-view__bottom">
        <section className="latest-view__bottom-main">
          <VideoCastSection
            title="Videocast"
            actionLabel="ver todos"
            href="/videocasts"
            items={videoCastItems}
            loading={loadingVideoCast}
          />
        </section>

        <aside className="latest-view__bottom-aside">
          <LatestNewsAside title="Últimas notícias" maxItems={4} />
        </aside>
      </div>
    </div>
  );
}
